/**
 * batch-62 garage-voertuig (FO-462 + FO-465) - PURE regels (geen Prisma, geen
 * I/O, geen `new Date()` buiten een meegegeven peildatum), gedeeld door de API
 * en de backoffice.
 *
 *   1. Kostencategorie-voorstel per garagefactuurregel: onderhoud vs
 *      schade/reparatie, uit de bestaande FO-329-classificatie + de regeltekst +
 *      de context (afspraak-soort, schademelding rond de datum). Gemengde
 *      facturen per regel; twijfelregels op een gemengde factuur krijgen GEEN
 *      categorie maar een keuze in de review (nooit gokken).
 *   2. Schadedossier-kandidaat: welke SchadeMelding hoort bij deze werkdatum.
 *   3. Werkdatum-afwijking (toekomst, ná de factuur, onwaarschijnlijk oud).
 *   4. Km-plausibiliteit: lager dan de vorige stand of onrealistisch hoog ->
 *      review, nooit overschrijven.
 *   5. Reminder "volgende actie": datum EN km-drempel, wat het eerst komt (km
 *      via een projectie op het gereden gemiddelde), plus de status
 *      actie vereist / gepland / op schema.
 */

import { schatOnderhoudsdatum } from './wagenpark-week'
import type {
  GarageKostenCategorie,
  OnderhoudRegelClassificatie,
  VoertuigAfspraakSoort,
  VoertuigOnderhoudType,
  VoertuigReminderSoort,
} from '@streep/types'

// ─── Interne datum-helpers (UTC-kalenderdagen) ───────────────────────────────

const DAG_MS = 86_400_000

function utcDag(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

/** Aantal kalenderdagen van `van` naar `tot` (negatief = `tot` ligt ervoor). */
function dagVerschil(van: Date, tot: Date): number {
  return Math.round((utcDag(tot) - utcDag(van)) / DAG_MS)
}

function plusDagen(d: Date, dagen: number): Date {
  return new Date(utcDag(d) + dagen * DAG_MS)
}

/**
 * `maanden` later op dezelfde dag, geklemd op de laatste dag van de doelmaand
 * (31-01 + 1 maand = 28/29-02, nooit een overloop naar maart).
 */
function plusMaanden(d: Date, maanden: number): Date {
  const jaar = d.getUTCFullYear()
  const maand = d.getUTCMonth() + maanden
  const doelJaar = jaar + Math.floor(maand / 12)
  const doelMaand = ((maand % 12) + 12) % 12
  const laatsteDag = new Date(Date.UTC(doelJaar, doelMaand + 1, 0)).getUTCDate()
  return new Date(Date.UTC(doelJaar, doelMaand, Math.min(d.getUTCDate(), laatsteDag)))
}

function isoDag(d: Date): string {
  return new Date(utcDag(d)).toISOString().slice(0, 10)
}

function parseIsoDag(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}/.test(iso)) return null
  const d = new Date(`${iso.slice(0, 10)}T00:00:00.000Z`)
  return Number.isNaN(d.getTime()) ? null : d
}

// ─── 1. Kostencategorie-voorstel (FO-462 §A) ─────────────────────────────────

/**
 * Trefwoorden die op schade of onzorgvuldigheid wijzen (correctief door schade:
 * plaatwerk, ruit, aanrijding). Een schade-treffer wint altijd van een
 * onderhouds-treffer in dezelfde regel ("remschijf vervangen na aanrijding").
 */
const SCHADE_TREFWOORDEN: readonly string[] = [
  'schade',
  'aanrijding',
  'plaatwerk',
  'spuitwerk',
  'spuiten',
  'lakwerk',
  'uitdeuken',
  'deuk',
  'kras',
  'bumper',
  'spiegelglas',
  'buitenspiegel',
  'voorruit',
  'zijruit',
  'achterruit',
  'ruit vervangen',
  'ster in',
  'sterretje',
  'barst',
  'gebarsten',
  'stoeprand',
  'velg beschadigd',
  'inbraak',
  'vandalisme',
  'hagel',
  'eigen risico',
]

/**
 * Trefwoorden van preventief onderhoud en normale slijtage (FO-462: beurten,
 * APK, banden-slijtage, vloeistoffen, slijtdelen). Slijtage-reparaties horen bij
 * de voertuigkost, niet bij de berijder.
 */
const ONDERHOUD_TREFWOORDEN: readonly string[] = [
  'beurt',
  'onderhoud',
  'olie',
  'filter',
  'remblok',
  'remschijf',
  'remvloeistof',
  'vloeistof',
  'ruitenwisser',
  'wisserblad',
  'accu',
  'distributieriem',
  'multiriem',
  'aandrijfriem',
  'bougie',
  'apk',
  'keuring',
  'roetmeting',
  'uitlijnen',
  'balanceren',
  'banden',
  'band ',
  'winterband',
  'zomerband',
  'ventiel',
  'airco',
  'uitlaat',
  'schokdemper',
  'lager',
  'fuseekogel',
  'stuurkogel',
  'draagarm',
  'koppeling',
  'waterpomp',
  'thermostaat',
  'startmotor',
  'dynamo',
  'software',
  'diagnose',
  'storing',
  'lamp',
  'verlichting',
]

function normaliseerRegeltekst(tekst: string): string {
  return ` ${tekst
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()} `
}

/**
 * Categorie uit de regeltekst alleen (null = geen eenduidig trefwoord). Een
 * schade-treffer wint van een onderhouds-treffer.
 */
export function categorieUitRegeltekst(tekst: string): GarageKostenCategorie | null {
  const t = normaliseerRegeltekst(tekst)
  if (t.trim().length === 0) return null
  if (SCHADE_TREFWOORDEN.some((w) => t.includes(w))) return 'SCHADE_REPARATIE'
  if (ONDERHOUD_TREFWOORDEN.some((w) => t.includes(w))) return 'ONDERHOUD'
  return null
}

interface RegelBasisVoorstel {
  categorie: GarageKostenCategorie | null
  zeker: boolean
  uitleg: string
}

function basisVoorstel(
  classificatie: OnderhoudRegelClassificatie,
  omschrijving: string,
): RegelBasisVoorstel {
  switch (classificatie) {
    case 'BEURT_KLEIN':
    case 'BEURT_GROOT':
    case 'APK_KEURING':
      return { categorie: 'ONDERHOUD', zeker: true, uitleg: 'Beurt of APK: preventief onderhoud.' }
    case 'SCHADE':
      return { categorie: 'SCHADE_REPARATIE', zeker: true, uitleg: 'Schadeherstel.' }
    case 'BANDEN': {
      const uitTekst = categorieUitRegeltekst(omschrijving)
      return uitTekst === 'SCHADE_REPARATIE'
        ? { categorie: 'SCHADE_REPARATIE', zeker: true, uitleg: 'Bandenschade herkend in de regeltekst.' }
        : { categorie: 'ONDERHOUD', zeker: true, uitleg: 'Banden: slijtage of seizoenswissel.' }
    }
    // batch-68 werkplaatsfacturen (FO-341 §3.3) - bijvullen, ruitenwisser, filter:
    // onderhoud, tenzij de regeltekst zelf op schade wijst (een ruit na een steen).
    case 'KLEIN_ONDERHOUD': {
      const uitTekst = categorieUitRegeltekst(omschrijving)
      return uitTekst === 'SCHADE_REPARATIE'
        ? { categorie: 'SCHADE_REPARATIE', zeker: true, uitleg: 'Schade herkend in de regeltekst.' }
        : { categorie: 'ONDERHOUD', zeker: true, uitleg: 'Klein onderhoud of bijvullen.' }
    }
    case 'REPARATIE':
    case 'OVERIG': {
      const uitTekst = categorieUitRegeltekst(omschrijving)
      if (uitTekst) {
        return {
          categorie: uitTekst,
          zeker: true,
          uitleg:
            uitTekst === 'SCHADE_REPARATIE'
              ? 'Schade herkend in de regeltekst.'
              : 'Slijtage of onderhoud herkend in de regeltekst.',
        }
      }
      return { categorie: null, zeker: false, uitleg: 'Niet eenduidig uit de regel af te leiden.' }
    }
  }
}

export interface KostenCategorieRegelInput {
  classificatie: OnderhoudRegelClassificatie
  omschrijving: string
}

export interface KostenCategorieContext {
  /** Soort van de afspraak waaraan de factuur hangt (indien bekend). */
  afspraakSoort?: VoertuigAfspraakSoort | null
  /** Is er een schademelding van dit voertuig rond de werkdatum? */
  schadeMeldingInVenster?: boolean
}

export interface KostenCategorieVoorstel {
  /** De geldende categorie; null = keuze vereist (twijfel op een gemengde of onduidelijke factuur). */
  kostenCategorie: GarageKostenCategorie | null
  /** Het voorstel (ook bij een open keuze: de voorselectie in de review). */
  voorstelCategorie: GarageKostenCategorie | null
  voorstelZeker: boolean
  keuzeVereist: boolean
  uitleg: string
}

/**
 * Deel de regels van één set (factuur × kenteken) in. Regels:
 *
 *   - eenduidige regels (beurt/APK/schade/banden, of een trefwoord) krijgen hun
 *     categorie;
 *   - twijfelregels op een factuur met precies één zekere categorie volgen die
 *     (de factuur-context beslist, geen gok);
 *   - twijfelregels op een GEMENGDE factuur krijgen geen categorie maar een
 *     keuze in de review (nooit gokken bij gemengd);
 *   - staat er geen enkele zekere regel op: de afspraak-soort beslist (SCHADE ->
 *     schade/reparatie, ONDERHOUD/APK -> onderhoud); zonder afspraak volgt een
 *     keuze (met een schademelding rond de datum als voorselectie schade).
 */
export function stelKostenCategorieenVoor(
  regels: readonly KostenCategorieRegelInput[],
  context: KostenCategorieContext = {},
): KostenCategorieVoorstel[] {
  const basis = regels.map((r) => basisVoorstel(r.classificatie, r.omschrijving))
  const zekereCategorieen = new Set<GarageKostenCategorie>()
  for (const b of basis) if (b.zeker && b.categorie) zekereCategorieen.add(b.categorie)
  const gemengd = zekereCategorieen.size > 1
  const enige = zekereCategorieen.size === 1 ? [...zekereCategorieen][0]! : null
  const afspraakCategorie: GarageKostenCategorie | null =
    context.afspraakSoort === 'SCHADE'
      ? 'SCHADE_REPARATIE'
      : context.afspraakSoort === 'ONDERHOUD' || context.afspraakSoort === 'APK'
        ? 'ONDERHOUD'
        : null
  const keuzeVoorselectie: GarageKostenCategorie =
    afspraakCategorie ?? (context.schadeMeldingInVenster ? 'SCHADE_REPARATIE' : 'ONDERHOUD')
  // FO-462 §6.2 - schade zonder factuurregel-detail (alleen "reparatie"): schade/reparatie.
  const alleenReparatie = regels.length > 0 && regels.every((r) => r.classificatie === 'REPARATIE')

  return basis.map<KostenCategorieVoorstel>((b, i) => {
    if (b.zeker && b.categorie) {
      return {
        kostenCategorie: b.categorie,
        voorstelCategorie: b.categorie,
        voorstelZeker: true,
        keuzeVereist: false,
        uitleg: b.uitleg,
      }
    }
    if (gemengd) {
      return {
        kostenCategorie: null,
        // Een twijfelende reparatieregel neigt naar schade (§6.2); de afspraak-soort wint.
        voorstelCategorie:
          !afspraakCategorie && regels[i]!.classificatie === 'REPARATIE'
            ? 'SCHADE_REPARATIE'
            : keuzeVoorselectie,
        voorstelZeker: false,
        keuzeVereist: true,
        uitleg: 'Gemengde factuur: kies voor deze regel onderhoud of schade/reparatie.',
      }
    }
    if (enige) {
      return {
        kostenCategorie: enige,
        voorstelCategorie: enige,
        voorstelZeker: true,
        keuzeVereist: false,
        uitleg: 'Volgt de rest van de factuur.',
      }
    }
    if (afspraakCategorie) {
      return {
        kostenCategorie: afspraakCategorie,
        voorstelCategorie: afspraakCategorie,
        voorstelZeker: true,
        keuzeVereist: false,
        uitleg:
          afspraakCategorie === 'SCHADE_REPARATIE'
            ? 'Hoort bij een schade-afspraak.'
            : 'Hoort bij een onderhouds- of APK-afspraak.',
      }
    }
    if (alleenReparatie) {
      return {
        kostenCategorie: 'SCHADE_REPARATIE',
        voorstelCategorie: 'SCHADE_REPARATIE',
        voorstelZeker: true,
        keuzeVereist: false,
        uitleg: 'Reparatie zonder nadere omschrijving: schade/reparatie (FO-462 §6.2).',
      }
    }
    return {
      kostenCategorie: null,
      voorstelCategorie: keuzeVoorselectie,
      voorstelZeker: false,
      keuzeVereist: true,
      uitleg: context.schadeMeldingInVenster
        ? 'Er is een schademelding rond deze datum: bevestig of dit schadeherstel is.'
        : 'Niet eenduidig uit de factuur af te leiden: kies onderhoud of schade/reparatie.',
    }
  })
}

/** Samenvattend label over de regels van een onderhoudsregel. */
export function kostenCategorieWeergave(
  categorieen: ReadonlyArray<GarageKostenCategorie | null>,
): 'ONDERHOUD' | 'SCHADE_REPARATIE' | 'GEMENGD' | 'NOG_IN_TE_DELEN' {
  if (categorieen.some((c) => c === null)) return 'NOG_IN_TE_DELEN'
  const set = new Set(categorieen)
  if (set.size > 1) return 'GEMENGD'
  if (set.has('SCHADE_REPARATIE')) return 'SCHADE_REPARATIE'
  if (set.has('ONDERHOUD')) return 'ONDERHOUD'
  return 'NOG_IN_TE_DELEN'
}

/**
 * Categorie van een onderhoudsregel ZONDER factuurregels (handmatig/import):
 * de eigen keuze, anders afgeleid uit het type.
 */
export function standaardKostenCategorieVoorType(
  type: VoertuigOnderhoudType,
): GarageKostenCategorie {
  return type === 'SCHADE' ? 'SCHADE_REPARATIE' : 'ONDERHOUD'
}

// ─── 2. Schadedossier-kandidaat (FO-462 §B4) ─────────────────────────────────

/** Een schademelding telt als "rond de datum" van 60 dagen vóór tot 3 dagen ná de werkdatum. */
export const SCHADE_DOSSIER_VENSTER = { dagenVoor: 60, dagenNa: 3 } as const

export interface SchadeMeldingKandidaat {
  id: string
  /** ISO-dag van de schade (NL-kalenderdag van `datumTijd`). */
  datum: string
}

export interface SchadeMeldingKeuze {
  /** De eenduidige melding, of null (geen, of twee op dezelfde dag). */
  gekozen: SchadeMeldingKandidaat | null
  /** Alle meldingen binnen het venster (suggesties voor de review). */
  inVenster: SchadeMeldingKandidaat[]
}

/**
 * Welke schademelding hoort bij deze werkdatum? De meest recente op of vóór de
 * werkdatum (de schade gaat aan het herstel vooraf); anders de eerste kort erna.
 * Twee meldingen op dezelfde (gekozen) dag = geen eenduidige keuze.
 */
export function kiesSchadeMelding(
  kandidaten: readonly SchadeMeldingKandidaat[],
  werkDatumIso: string,
  venster: { dagenVoor: number; dagenNa: number } = SCHADE_DOSSIER_VENSTER,
): SchadeMeldingKeuze {
  const werk = parseIsoDag(werkDatumIso)
  if (!werk) return { gekozen: null, inVenster: [] }
  const inVenster = kandidaten
    .filter((k) => {
      const d = parseIsoDag(k.datum)
      if (!d) return false
      const verschil = dagVerschil(werk, d)
      return verschil >= -venster.dagenVoor && verschil <= venster.dagenNa
    })
    .sort((a, b) => a.datum.localeCompare(b.datum))

  const ervoor = inVenster.filter((k) => k.datum.slice(0, 10) <= werkDatumIso)
  const kiesUit = ervoor.length > 0 ? ervoor : inVenster
  if (kiesUit.length === 0) return { gekozen: null, inVenster }
  const doel = ervoor.length > 0 ? kiesUit[kiesUit.length - 1]! : kiesUit[0]!
  const zelfdeDag = kiesUit.filter((k) => k.datum.slice(0, 10) === doel.datum.slice(0, 10))
  return { gekozen: zelfdeDag.length === 1 ? doel : null, inVenster }
}

// ─── 3. Werkdatum-afwijking (FO-465 §D.9) ────────────────────────────────────

export type WerkdatumAfwijking =
  | { soort: 'TOEKOMST'; dagen: number }
  | { soort: 'NA_FACTUUR'; dagen: number }
  | { soort: 'ZEER_OUD'; dagen: number }
  | null

/** Werk ná de factuurdatum mag een paar dagen (factuur vooraf opgemaakt). */
const WERK_NA_FACTUUR_MARGE_DAGEN = 7
/** Werk meer dan een jaar vóór de factuurdatum is vrijwel altijd een leesfout. */
const WERK_ZEER_OUD_DAGEN = 365

/**
 * Een late factuur (werk ruim vóór de factuurdatum) is GEEN afwijking: de
 * werkdatum is leidend (FO-465 §5.3). Wel een afwijking: werk in de toekomst,
 * werk duidelijk ná de factuurdatum, of een onwaarschijnlijk oude werkdatum.
 */
export function werkdatumAfwijking(
  werkDatumIso: string,
  factuurdatumIso: string,
  vandaagIso: string,
): WerkdatumAfwijking {
  const werk = parseIsoDag(werkDatumIso)
  const factuur = parseIsoDag(factuurdatumIso)
  const vandaag = parseIsoDag(vandaagIso)
  if (!werk || !factuur || !vandaag) return null
  if (werk.getTime() > vandaag.getTime()) {
    return { soort: 'TOEKOMST', dagen: dagVerschil(vandaag, werk) }
  }
  const voorFactuur = dagVerschil(werk, factuur)
  if (voorFactuur < -WERK_NA_FACTUUR_MARGE_DAGEN) return { soort: 'NA_FACTUUR', dagen: -voorFactuur }
  if (voorFactuur > WERK_ZEER_OUD_DAGEN) return { soort: 'ZEER_OUD', dagen: voorFactuur }
  return null
}

// ─── 4. Km-plausibiliteit (FO-465 §C.6d) ─────────────────────────────────────

/** Meer dan dit per dag gemiddeld is voor een bedrijfsauto onrealistisch. */
export const KM_PLAUSIBEL_MAX_PER_DAG = 1500
/** Zonder datum bij de vorige stand: een sprong boven deze grens is onrealistisch. */
export const KM_PLAUSIBEL_MAX_SPRONG = 200_000

export interface KmMeetpunt {
  km: number
  datum: Date | null
}

export type KmOordeel =
  | { oordeel: 'OK' }
  | { oordeel: 'LAGER'; vorige: KmMeetpunt }
  | {
      oordeel: 'ONREALISTISCH'
      vorige: KmMeetpunt | null
      volgende: KmMeetpunt | null
      kmPerDag: number | null
    }

/**
 * Toets een nieuwe km-stand tegen de stand vlak ervoor (`vorige`, op of vóór de
 * datum) en de eerstvolgende latere stand (`volgende`). Lager dan de vorige =
 * LAGER; hoger dan een latere stand of meer dan het realistische dag-gemiddelde
 * = ONREALISTISCH. Beide gaan naar de review, nooit over een bestaande stand heen.
 */
export function beoordeelKmStand(
  nieuw: { km: number; datum: Date },
  vorige: KmMeetpunt | null,
  volgende: KmMeetpunt | null = null,
): KmOordeel {
  if (vorige && nieuw.km < vorige.km) return { oordeel: 'LAGER', vorige }
  if (volgende && nieuw.km > volgende.km) {
    return { oordeel: 'ONREALISTISCH', vorige, volgende, kmPerDag: null }
  }
  if (vorige) {
    const delta = nieuw.km - vorige.km
    if (vorige.datum) {
      const dagen = Math.max(0, dagVerschil(vorige.datum, nieuw.datum))
      const perDag = dagen === 0 ? delta : delta / dagen
      if (perDag > KM_PLAUSIBEL_MAX_PER_DAG) {
        return {
          oordeel: 'ONREALISTISCH',
          vorige,
          volgende,
          kmPerDag: Math.round(perDag),
        }
      }
    } else if (delta > KM_PLAUSIBEL_MAX_SPRONG) {
      return { oordeel: 'ONREALISTISCH', vorige, volgende, kmPerDag: null }
    }
  }
  return { oordeel: 'OK' }
}

// ─── 5. Reminders "volgende actie" (FO-465 §C.6e) ────────────────────────────

/**
 * Gemiddeld aantal km per dag uit de km-historie (laatste jaar vóór de
 * peildatum; minder dan twee punten in dat jaar = de hele historie). Null
 * wanneer het bereik korter is dan 14 dagen, de stand niet oploopt of het
 * gemiddelde onrealistisch is.
 */
export function schatKmPerDag(
  punten: ReadonlyArray<{ datum: Date; km: number }>,
  peil: Date,
): number | null {
  const gesorteerd = [...punten]
    .filter((p) => p.datum.getTime() <= peil.getTime())
    .sort((a, b) => a.datum.getTime() - b.datum.getTime())
  const jaarGrens = plusDagen(peil, -365)
  const recent = gesorteerd.filter((p) => p.datum.getTime() >= jaarGrens.getTime())
  const bron = recent.length >= 2 ? recent : gesorteerd
  if (bron.length < 2) return null
  const eerste = bron[0]!
  const laatste = bron[bron.length - 1]!
  const dagen = dagVerschil(eerste.datum, laatste.datum)
  const km = laatste.km - eerste.km
  if (dagen < 14 || km <= 0) return null
  const perDag = km / dagen
  return perDag > KM_PLAUSIBEL_MAX_PER_DAG ? null : perDag
}

export interface ReminderVervalInput {
  /** Werkdatum van de laatste uitvoering. */
  basisDatum: Date
  /** Km-stand bij de laatste uitvoering (null = onbekend). */
  basisKm: number | null
  intervalMaanden: number | null
  intervalKm: number | null
  /** Gemiddelde km per dag (null = geen projectie mogelijk). */
  kmPerDag: number | null
  /**
   * batch-70 wagenpark-week (FO-528 §C7) - het gemiddelde weektempo van de laatste 8
   * weken (uit de wekelijkse km-standen). Wint van `kmPerDag` (de jaarhistorie): met
   * wekelijkse standen is de schatting betrouwbaar. Via `schatOnderhoudsdatum`.
   */
  weekTempo?: number | null
  /** De meest recente bekende stand (projecteert vanaf hier als die na de basis ligt). */
  laatsteKm?: { km: number; datum: Date } | null
  /**
   * Een vaste vervaldatum die het datum-interval vervangt (APK: de bekende
   * vervaldatum van het voertuig of het bevestigde voorstel).
   */
  vasteVervaldatum?: Date | null
}

export interface ReminderVerval {
  vervalDatum: Date | null
  vervalKm: number | null
  /** Het moment waarop de km-drempel naar verwachting bereikt wordt. */
  geschatteKmDatum: Date | null
  /** De eerste van beide drempels. */
  effectieveDatum: Date | null
  bepalend: 'DATUM' | 'KM' | null
}

/**
 * Datum EN km-drempel, wat het eerst komt (FO-465 §6e/T5). Voorbeeld: grote
 * beurt 24 maanden / 40.000 km bij 100 km per dag -> de km-drempel valt na ~400
 * dagen, dus vóór de 24 maanden: de reminder volgt de km.
 */
export function berekenReminderVerval(input: ReminderVervalInput): ReminderVerval {
  const vervalDatum =
    input.vasteVervaldatum ??
    (input.intervalMaanden ? plusMaanden(input.basisDatum, input.intervalMaanden) : null)
  const vervalKm =
    input.intervalKm != null && input.basisKm != null ? input.basisKm + input.intervalKm : null

  let geschatteKmDatum: Date | null = null
  if (vervalKm != null) {
    const vanaf =
      input.laatsteKm &&
      input.laatsteKm.datum.getTime() >= input.basisDatum.getTime() &&
      input.laatsteKm.km >= (input.basisKm ?? 0)
        ? input.laatsteKm
        : { km: input.basisKm ?? 0, datum: input.basisDatum }
    const resterend = vervalKm - vanaf.km
    if (resterend <= 0) {
      geschatteKmDatum = new Date(utcDag(vanaf.datum))
    } else if (input.weekTempo != null && input.weekTempo > 0) {
      geschatteKmDatum = schatOnderhoudsdatum({
        vervalKm,
        huidigeKm: vanaf.km,
        weekTempo: input.weekTempo,
        vanaf: vanaf.datum,
      }).datum
    } else if (input.kmPerDag && input.kmPerDag > 0) {
      geschatteKmDatum = plusDagen(vanaf.datum, Math.ceil(resterend / input.kmPerDag))
    }
  }

  let effectieveDatum: Date | null = null
  let bepalend: 'DATUM' | 'KM' | null = null
  if (vervalDatum && geschatteKmDatum) {
    if (geschatteKmDatum.getTime() < vervalDatum.getTime()) {
      effectieveDatum = geschatteKmDatum
      bepalend = 'KM'
    } else {
      effectieveDatum = vervalDatum
      bepalend = 'DATUM'
    }
  } else if (vervalDatum) {
    effectieveDatum = vervalDatum
    bepalend = 'DATUM'
  } else if (geschatteKmDatum) {
    effectieveDatum = geschatteKmDatum
    bepalend = 'KM'
  }
  return { vervalDatum, vervalKm, geschatteKmDatum, effectieveDatum, bepalend }
}

export interface ReminderStatusInput {
  effectieveDatum: Date | null
  vervalKm: number | null
  huidigeKm: number | null
  drempelDagen: number
  drempelKm: number
  /**
   * Staat er een GEPLANDE afspraak (met datum) van de dekkende soort? Een afspraak
   * die nog TE_PLANNEN is dekt de reminder niet: de actie blijft "actie vereist".
   */
  geplandeAfspraak: boolean
  vandaag: Date
}

export interface ReminderStatus {
  status: 'ACTIE_VEREIST' | 'GEPLAND' | 'OK'
  dagenTot: number | null
  kmTot: number | null
  /** De drempel (datum of km) is bereikt. */
  binnenDrempel: boolean
}

/**
 * Dezelfde lezing als de FO-435-actie-status: staat er een afspraak, dan
 * "gepland"; valt de actie binnen de drempel zonder afspraak, dan "actie
 * vereist"; anders op schema.
 */
export function bepaalReminderStatus(input: ReminderStatusInput): ReminderStatus {
  const dagenTot = input.effectieveDatum ? dagVerschil(input.vandaag, input.effectieveDatum) : null
  const kmTot =
    input.vervalKm != null && input.huidigeKm != null ? input.vervalKm - input.huidigeKm : null
  const binnenDrempel =
    (dagenTot !== null && dagenTot <= input.drempelDagen) ||
    (kmTot !== null && kmTot <= input.drempelKm)
  const status = input.geplandeAfspraak ? 'GEPLAND' : binnenDrempel ? 'ACTIE_VEREIST' : 'OK'
  return { status, dagenTot, kmTot, binnenDrempel }
}

/**
 * Welke "volgende acties" worden gereset door een uitgevoerde set? Een grote
 * beurt omvat een kleine beurt (beide resetten); APK en banden elk hun eigen.
 */
export function reminderSoortenVoorClassificaties(
  classificaties: readonly OnderhoudRegelClassificatie[],
): VoertuigReminderSoort[] {
  const uit = new Set<VoertuigReminderSoort>()
  if (classificaties.includes('BEURT_GROOT')) {
    uit.add('GROTE_BEURT')
    uit.add('KLEINE_BEURT')
  }
  if (classificaties.includes('BEURT_KLEIN')) uit.add('KLEINE_BEURT')
  if (classificaties.includes('APK_KEURING')) uit.add('APK')
  if (classificaties.includes('BANDEN')) uit.add('BANDEN')
  return [...uit]
}

/** Idem voor een onderhoudsregel zonder factuurregels (handmatig afgerond). */
export function reminderSoortenVoorOnderhoudType(
  type: VoertuigOnderhoudType,
): VoertuigReminderSoort[] {
  switch (type) {
    case 'APK':
      return ['APK']
    case 'ONDERHOUDSBEURT':
      return ['KLEINE_BEURT']
    case 'BANDEN':
      return ['BANDEN']
    default:
      return []
  }
}

/**
 * Cyclus-sleutel: taak en signaal worden één keer per (soort, basis) aangemaakt,
 * met de basis = de laatste uitvoering. Alleen een nieuwe uitvoering (of een
 * gecorrigeerde werkdatum) start een nieuwe cyclus; een verschuivende km-projectie
 * werkt de open taak bij en maakt nooit een tweede (review batch-62, B1).
 */
export function reminderCyclusSleutel(
  soort: VoertuigReminderSoort,
  basisDatum: Date | null,
): string {
  return `${soort}:${basisDatum ? isoDag(basisDatum) : 'onbekend'}`
}

/** "APK plannen voor 12-ABC-3 vóór 12-10-2026" (FO-465 §8). */
export function reminderTaakTitel(
  soortLabel: string,
  kentekenWeergave: string,
  vervaldatum: Date | null,
): string {
  if (!vervaldatum) return `${soortLabel} plannen voor ${kentekenWeergave}`
  const d = isoDag(vervaldatum)
  return `${soortLabel} plannen voor ${kentekenWeergave} vóór ${d.slice(8, 10)}-${d.slice(5, 7)}-${d.slice(0, 4)}`
}
